// apps/web/src/lib/vendas/formatacao.ts — Fase 12, Plano 02 (PV-01, D-37).
// Formatação pt-BR única das telas de Vendas (funções puras). Nenhuma página formata
// data, moeda ou quantidade por conta própria.

const TRACO = '—';
const DATA_ISO = /^(\d{4})-(\d{2})-(\d{2})/;
/** Unidades de contagem: quantidade sempre inteira. */
const UNIDADES_INTEIRAS = new Set(['peça', 'peca', 'pç', 'pc', 'un']);
/** Casas máximas de quantidade: as 4 que a API aceita (DTO de PV, recebimento e fechamento). WR-D2-02. */
export const CASAS_MAXIMAS_QUANTIDADE = 4;

/** Troca o espaço não separável do Intl (U+00A0 / U+202F) por espaço comum. */
function normalizarEspaco(texto: string): string {
  return texto.replace(/[  ]/g, ' ');
}

/** `AAAA-MM-DD` (ou ISO com horário) para `dd/MM/yyyy`, sem passar por Date (sem deslocamento de fuso). */
export function formatarData(iso: string | null | undefined): string {
  if (!iso) return TRACO;
  const partes = DATA_ISO.exec(iso);
  if (!partes) return iso;
  return `${partes[3]}/${partes[2]}/${partes[1]}`;
}

/** Moeda BRL com `casas` decimais (padrão 2; preço unitário de tinta admite 4). */
export function formatarMoeda(valor: number | null | undefined, casas = 2): string {
  if (valor === null || valor === undefined) return TRACO;
  return normalizarEspaco(
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    }).format(valor),
  );
}

/** Unidade de contagem (peça, pç, pc, un): a quantidade é inteira. */
export function unidadeInteira(unidade: string): boolean {
  return UNIDADES_INTEIRAS.has(unidade.trim().toLowerCase());
}

const FORMATO_QUANTIDADE = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: CASAS_MAXIMAS_QUANTIDADE });

/**
 * Quantidade com até 4 casas (a precisão que a API aceita), sempre a MESMA regra em todas as telas. Peça/pç/un é
 * inteira; se vier fracionada (dado legado) mostra as casas em vez de arredondar e esconder a diferença.
 */
export function formatarQuantidade(valor: number | null | undefined, _unidade = ''): string {
  if (valor === null || valor === undefined) return TRACO;
  return FORMATO_QUANTIDADE.format(valor);
}

/** Percentual com vírgula decimal (`12,5%`). */
export function formatarPercentual(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return TRACO;
  return `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(valor)}%`;
}

/** 'peça' vira 'peças' no plural (1 fica no singular); demais unidades seguem como cadastradas. */
export function unidadePorExtenso(quantidade: number, unidade: string): string {
  const chave = unidade.trim().toLowerCase();
  if (chave === 'peça' || chave === 'peca' || chave === 'pç' || chave === 'pc') return quantidade === 1 ? 'peça' : 'peças';
  return unidade;
}

/** Fuso de negócio das telas de Vendas (o servidor audita e calcula "hoje" nele, não no relógio do navegador). */
export const FUSO_VENDAS = 'America/Sao_Paulo';

function partesNoFuso(data: Date, comHora: boolean): Record<string, string> {
  const formato = new Intl.DateTimeFormat('pt-BR', {
    timeZone: FUSO_VENDAS,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(comHora ? { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' as const } : {}),
  });
  const partes: Record<string, string> = {};
  for (const parte of formato.formatToParts(data)) partes[parte.type] = parte.value;
  return partes;
}

/** `AAAA-MM-DD` de hoje em America/Sao_Paulo (WR-D2-03): não depende do fuso do navegador. */
export function hojeIso(agora: Date = new Date()): string {
  const p = partesNoFuso(agora, false);
  return `${p.year}-${p.month}-${p.day}`;
}

/**
 * Instante ISO (UTC) para `dd/MM/yyyy` no dia de São Paulo (WR-D1-09). Use para timestamps (`createdAt`, `recebidoEm`);
 * `formatarData` fica para datas puras (`AAAA-MM-DD`). Texto que não é data volta como veio.
 */
export function formatarDataLocal(iso: string | null | undefined): string {
  if (!iso) return TRACO;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  const p = partesNoFuso(data, false);
  return `${p.day}/${p.month}/${p.year}`;
}

/** Instante ISO para `dd/MM/yyyy HH:mm` em America/Sao_Paulo; texto inválido volta como veio. */
export function formatarDataHora(iso: string): string {
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return iso;
  const p = partesNoFuso(data, true);
  return `${p.day}/${p.month}/${p.year} ${p.hour}:${p.minute}`;
}
